"use client";

import { useState, useEffect } from "react";
import { WifiOff, Wifi, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function OfflineScreen() {
  const [isOffline, setIsOffline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    // Check initial online status
    const checkConnection = () => {
      const isOnline = navigator.onLine;
      setIsOffline(!isOnline);
    };

    checkConnection();

    const handleOffline = () => {
      setIsOffline(true);
      setShowReconnected(false);
    };

    const handleOnline = () => {
      setIsOffline(false);
      setIsRetrying(false);
      setShowReconnected(true);

      // Hide the "reconnected" message after 2 seconds
      setTimeout(() => {
        setShowReconnected(false);
      }, 2000);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);

    // Simulate checking connection
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Check if we're actually online now
    if (navigator.onLine) {
      setIsOffline(false);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 2000);
    }

    setIsRetrying(false);
  };

  // Show reconnected overlay briefly
  if (showReconnected) {
    return (
      <div className='fixed inset-0 bg-green-500/90 backdrop-blur-sm z-[9999] flex items-center justify-center'>
        <Card className='w-full max-w-md mx-4 bg-white/95 backdrop-blur-sm shadow-2xl border-green-200'>
          <CardContent className='flex flex-col items-center justify-center p-8 text-center'>
            <div className='w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4'>
              <Wifi className='h-8 w-8 text-white' />
            </div>
            <h2 className='text-2xl font-semibold text-green-800 mb-2'>
              Back Online!
            </h2>
            <p className='text-green-600'>Your connection has been restored.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Don't show anything if online
  if (!isOffline) {
    return null;
  }

  return (
    <div className='fixed inset-0 bg-black/85 backdrop-blur-sm z-[9999] flex items-center justify-center'>
      <Card className='w-full max-w-md mx-4 bg-white/95 backdrop-blur-sm shadow-2xl'>
        <CardHeader className='text-center pb-4'>
          <div className='w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4'>
            <WifiOff className='h-8 w-8 text-white' />
          </div>
          <CardTitle className='text-2xl font-semibold text-gray-900'>
            No Internet Connection
          </CardTitle>
          <CardDescription className='text-gray-600 mt-2'>
            Please check your internet connection and try again.
          </CardDescription>
        </CardHeader>
        <CardContent className='text-center'>
          <Button
            onClick={handleRetry}
            disabled={isRetrying}
            className='w-full'
            size='lg'
          >
            {isRetrying ? (
              <>
                <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                Checking Connection...
              </>
            ) : (
              <>
                <RefreshCw className='mr-2 h-4 w-4' />
                Try Again
              </>
            )}
          </Button>
          <p className='text-sm text-gray-500 mt-4'>
            This screen will automatically disappear when your connection is
            restored.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
